import React, { useEffect, useRef, useState, memo, CSSProperties } from "react";
import { useTranslation } from "react-i18next";

const isFirefox =
  typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent);

interface SmartVideoPlayerProps {
  videoUrl: string;

  /** Start the element muted? (ignored if startUnmuted=true) */
  muted?: boolean;

  loop?: boolean;
  controls?: boolean;
  preload?: "auto" | "metadata" | "none";

  /** Try to (auto)play when visible via IntersectionObserver. Default: true */
  autoPlayWhenVisible?: boolean;

  /** If true, never pause due to IO visibility (e.g., in lightbox). */
  noVisibilityPause?: boolean;

  /** If within N seconds of end, ignore IO pause. */
  suppressPauseNearEndSec?: number;

  /** Debounce IO pause to avoid flicker in Firefox. */
  pauseDebounceMs?: number;

  /** IO root margin. */
  visibilityRootMargin?: string;

  /** --- NEW: start with sound ON (no muted); won't attempt autoplay (browser blocks).
      Users press play once and audio works immediately. Default: false. */
  startUnmuted?: boolean;

  /** --- NEW: allow programmatic autoplay attempts. Default: true.
      If startUnmuted=true and autoplay=true, the browser will block and we’ll show the overlay. */
  autoplay?: boolean;

  containerStyle?: CSSProperties;
  videoStyle?: CSSProperties;
  videoProps?: React.VideoHTMLAttributes<HTMLVideoElement> & Record<string, any>;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

const SmartVideoPlayer: React.FC<SmartVideoPlayerProps> = ({
  videoUrl,
  muted = true,
  loop = false,
  controls = false,
  preload = "metadata",
  autoPlayWhenVisible = true,
  noVisibilityPause = false,
  suppressPauseNearEndSec = 0,
  pauseDebounceMs = 300,
  visibilityRootMargin = "600px 0px",

  // NEW
  startUnmuted = false,
  autoplay = true,

  containerStyle,
  videoStyle,
  videoProps,
  containerProps,
  onClick,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  const [isVisible, setIsVisible] = useState(false);
  const nearEndRef = useRef(false);
  const pauseTimerRef = useRef<number | null>(null);

  // When a browser blocks autoplay-with-audio, we flip this on and show controls/overlay.
  const [needsGesture, setNeedsGesture] = useState(false);

  // --- Firefox gapless loop hack state ---
  const preLoopJumpedRef = useRef(false); // prevent multiple jumps per cycle

  // Effective muted value (prop OR startUnmuted override)
  const effectiveMuted = startUnmuted ? false : !!muted;

  // Track near-end continuously (and reuse for IO guard)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      if (!suppressPauseNearEndSec) {
        nearEndRef.current = false;
        return;
      }
      const d = v.duration;
      nearEndRef.current =
        Number.isFinite(d) && d > 0 && d - v.currentTime <= suppressPauseNearEndSec;
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [suppressPauseNearEndSec]);

  // Intersection Observer
  useEffect(() => {
    if (!autoPlayWhenVisible) return;
    const node = containerRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => setIsVisible(entries[0]?.isIntersecting ?? false),
      { root: null, rootMargin: visibilityRootMargin, threshold: 0.01 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [autoPlayWhenVisible, visibilityRootMargin]);

  // Visibility-driven Play/Pause with debounce + near-end guard
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !autoPlayWhenVisible) return;

    if (pauseTimerRef.current) {
      window.clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }

    // Only attempt autoplay if allowed by prop.
    if (isVisible) {
      if (v.paused && autoplay) {
        v.muted = effectiveMuted;
        v.play()
          .then(() => setNeedsGesture(false))
          .catch(() => setNeedsGesture(true)); // likely blocked (unmuted)
      }
    } else {
      if (noVisibilityPause || nearEndRef.current) return;
      pauseTimerRef.current = window.setTimeout(() => {
        if (!noVisibilityPause && !nearEndRef.current) v.pause();
        pauseTimerRef.current = null;
      }, pauseDebounceMs);
    }
  }, [
    isVisible,
    autoPlayWhenVisible,
    noVisibilityPause,
    pauseDebounceMs,
    effectiveMuted,
    autoplay,
  ]);

  // When URL / mute flags change
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    setNeedsGesture(false);
    v.muted = effectiveMuted;
    v.load();

    // Optional initial autoplay attempt (often blocked if unmuted)
    if (autoPlayWhenVisible && autoplay) {
      v.play().catch(() => setNeedsGesture(true));
    }
  }, [videoUrl, effectiveMuted, autoPlayWhenVisible, autoplay]);

  // Manual start with sound (overlay / user gesture)
  const onUserStartWithSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false; // ensure sound
    setNeedsGesture(false);
    v.play().catch(() => {});
  };

  // Native loop for non-Firefox; manual loop for Firefox
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // Reset the guard shortly after a successful loop
    const resetGuardIfAtStart = () => {
      if (v.currentTime < 0.1) preLoopJumpedRef.current = false;
    };

    // Manual loop: jump a bit before the end to avoid FF stall
    const onTimeupdateManualLoop = () => {
      if (!isFirefox || !loop) return;
      const d = v.duration;
      if (!Number.isFinite(d) || d <= 0) return;

      const remaining = d - v.currentTime;
      // Adjust threshold as needed (0.25–0.5s works well)
      if (remaining <= 0.1 && !preLoopJumpedRef.current) {
        preLoopJumpedRef.current = true;
        try {
          v.currentTime = 0.01; // avoid exact 0s
          v.play().catch(() => {});
        } catch {}
      }
    };

    // Fallbacks: if FF emits waiting/stalled near end, force the jump
    const forceJumpIfNearEnd = () => {
      if (!isFirefox || !loop) return;
      const d = v.duration;
      if (!Number.isFinite(d) || d <= 0) return;
      const remaining = d - v.currentTime;
      if (remaining <= 1.0) {
        preLoopJumpedRef.current = true;
        try {
          v.currentTime = 0.01;
          v.play().catch(() => {});
        } catch {}
      }
    };

    const onEndedNative = () => {
      if (isFirefox && loop) {
        preLoopJumpedRef.current = true;
        try {
          v.currentTime = 0.01;
          v.play().catch(() => {});
        } catch {}
      }
    };

    v.addEventListener("timeupdate", onTimeupdateManualLoop);
    v.addEventListener("waiting", forceJumpIfNearEnd);
    v.addEventListener("stalled", forceJumpIfNearEnd);
    v.addEventListener("ended", onEndedNative);
    v.addEventListener("seeked", resetGuardIfAtStart);

    return () => {
      v.removeEventListener("timeupdate", onTimeupdateManualLoop);
      v.removeEventListener("waiting", forceJumpIfNearEnd);
      v.removeEventListener("stalled", forceJumpIfNearEnd);
      v.removeEventListener("ended", onEndedNative);
      v.removeEventListener("seeked", resetGuardIfAtStart);
    };
  }, [loop]);

  // Force fuller buffer on Firefox (helps reduce end-of-stream hiccup)
  const effectivePreload = isFirefox ? "auto" : preload;

  return (
    <div
      ref={containerRef}
      {...containerProps}
      onClick={onClick}
      style={{
        position: "relative",
        height: "80vh",
        width: "auto",
        maxWidth: "95vw",
        borderRadius: 12,
        backgroundColor: "#000",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...containerStyle,
      }}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        muted={effectiveMuted}
        // IMPORTANT: disable native loop on Firefox to avoid the 1s hang
        loop={isFirefox ? false : loop}
        // If autoplay was blocked, expose native controls so users can start it
        controls={needsGesture ? true : controls}
        playsInline
        preload={effectivePreload}
        {...videoProps}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          objectFit: "contain",
          display: "block",
          backgroundColor: "#000",
          ...videoStyle,
        }}
        onError={(e) => console.warn("Video failed:", videoUrl, e)}
      />

      {/* Gesture overlay: only when autoplay failed AND controls are hidden by design */}
      {needsGesture && !controls && (
        <button
          onClick={onUserStartWithSound}
          title={t("smartVideo.playWithSound")}
          aria-label={t("smartVideo.playWithSound")}
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            background: "linear-gradient(0deg, rgba(0,0,0,0.35), rgba(0,0,0,0.35))",
            border: "none",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "9999px",
              display: "grid",
              placeItems: "center",
              background: "rgba(0,0,0,0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "#fff",
              backdropFilter: "blur(6px)",
            }}
          >
            {/* Play icon */}
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </button>
      )}
    </div>
  );
};

export default memo(SmartVideoPlayer);
